import usePageTitle from "../utils/usePageTitle";
import React, { useEffect, useMemo, useState } from "react";
import { Form, Formik, getIn, useFormikContext } from "formik";
import { Link, useParams } from "react-router-dom";
import {
  Button,
  Container,
  ErrorBlockGeneric,
  Select,
  SelectMultiple,
  Switch,
} from "common-tailwind-components";
import { ShieldCheck } from "lucide-react";
import { useTranslation } from "react-i18next";

import {
  get_user_permissions,
  get_users,
  set_user_permissions,
  splitApiRequestFormErrors,
} from "../api";
import { useLogin } from "../reducers";
import type { ClankPermissions, SpecifyUserPermission, User } from "../api/types";
import { isAdmin } from "../utils/permissions";

type PermissionMode = "OnlyMine" | "AllCompany" | "SpecificUsers" | "All";

type SpecifyUserPermissionFormValues = {
  mode: PermissionMode;
  SpecificUsers: string[];
};

type PermissionFormValues = {
  is_admin: boolean;
  sharedVisualization: SpecifyUserPermissionFormValues;
  recordings: {
    visualization: SpecifyUserPermissionFormValues;
  };
  processes: {
    visualization: SpecifyUserPermissionFormValues;
  };
  screenshots: {
    visualization: SpecifyUserPermissionFormValues;
  };
  users: {
    visualization: SpecifyUserPermissionFormValues;
    creation: boolean;
  };
};

type Choice = {
  id: PermissionMode;
  display: string;
};

const permissionMode = (permission: SpecifyUserPermission): PermissionMode =>
  typeof permission === "string" ? permission : "SpecificUsers";

const specificUsers = (permission: SpecifyUserPermission): string[] =>
  typeof permission === "string" ? [] : permission.SpecificUsers;

const permissionToForm = (permission: SpecifyUserPermission): SpecifyUserPermissionFormValues => ({
  mode: permissionMode(permission),
  SpecificUsers: specificUsers(permission),
});

const permissionsToForm = (permissions: ClankPermissions): PermissionFormValues => {
  const recordings = permissionToForm(permissions.recordings.visualization);
  return {
    is_admin: permissions.is_admin,
    sharedVisualization: recordings,
    recordings: {
      visualization: recordings,
    },
    processes: {
      visualization: permissionToForm(permissions.processes.visualization),
    },
    screenshots: {
      visualization: permissionToForm(permissions.screenshots.visualization),
    },
    users: {
      visualization: permissionToForm(permissions.users.visualization),
      creation: permissions.users.creation,
    },
  };
};

const specifyPermission = (permission: SpecifyUserPermissionFormValues): SpecifyUserPermission => {
  if (permission.mode === "SpecificUsers") {
    return { SpecificUsers: permission.SpecificUsers };
  }
  return permission.mode;
};

const formToPermissions = (
  values: PermissionFormValues,
  simplifiedMode: boolean,
): ClankPermissions => {
  const sharedVisualization = specifyPermission(values.sharedVisualization);
  return {
    is_admin: values.is_admin,
    recordings: {
      visualization: simplifiedMode
        ? sharedVisualization
        : specifyPermission(values.recordings.visualization),
    },
    processes: {
      visualization: simplifiedMode
        ? sharedVisualization
        : specifyPermission(values.processes.visualization),
    },
    screenshots: {
      visualization: simplifiedMode
        ? sharedVisualization
        : specifyPermission(values.screenshots.visualization),
    },
    users: {
      creation: values.users.creation,
      visualization: simplifiedMode
        ? sharedVisualization
        : specifyPermission(values.users.visualization),
    },
  };
};

const sameUsers = (left: string[], right: string[]) => {
  const sortedLeft = [...left].sort();
  const sortedRight = [...right].sort();
  return (
    sortedLeft.length === sortedRight.length &&
    sortedLeft.every((id, index) => id === sortedRight[index])
  );
};

const samePermission = (
  left: SpecifyUserPermissionFormValues,
  right: SpecifyUserPermissionFormValues,
) =>
  left.mode === right.mode &&
  (left.mode !== "SpecificUsers" || sameUsers(left.SpecificUsers, right.SpecificUsers));

const canUseSimplifiedPermissions = (values: PermissionFormValues) =>
  samePermission(values.recordings.visualization, values.processes.visualization) &&
  samePermission(values.recordings.visualization, values.screenshots.visualization) &&
  samePermission(values.recordings.visualization, values.users.visualization);

const displayUser = (user: User) => [user.username, user.email].filter(Boolean).join(" - ");

const PermissionSection = ({
  title,
  scopeLabel,
  usersLabel,
  visualizationName,
  users,
  scopeChoices,
}: {
  title: string;
  scopeLabel: string;
  usersLabel: string;
  visualizationName:
    | "recordings.visualization"
    | "processes.visualization"
    | "screenshots.visualization"
    | "users.visualization";
  users: User[];
  scopeChoices: Choice[];
}) => {
  const { values } = useFormikContext<PermissionFormValues>();
  const modeName = `${visualizationName}.mode`;
  const specificUsersName = `${visualizationName}.SpecificUsers`;
  return (
    <section className="rounded-xl border border-outline bg-muted-surface/30 p-4">
      <h2 className="mb-3 text-sm font-semibold text-text">{title}</h2>
      <Select name={modeName} label={scopeLabel} choices={scopeChoices} required />
      {getIn(values, modeName) === "SpecificUsers" && (
        <SelectMultiple
          name={specificUsersName}
          label={usersLabel}
          choices={users}
          displayChoice={displayUser}
        />
      )}
    </section>
  );
};

const UsersSpecificSelector = ({ users }: { users: User[] }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<PermissionFormValues>();

  if (values.users.visualization.mode !== "SpecificUsers") {
    return null;
  }

  return (
    <SelectMultiple
      name="users.visualization.SpecificUsers"
      label={t("specific_users")}
      choices={users}
      displayChoice={displayUser}
    />
  );
};

const SimplifiedSpecificSelector = ({ users }: { users: User[] }) => {
  const { t } = useTranslation();
  const { values } = useFormikContext<PermissionFormValues>();

  if (values.sharedVisualization.mode !== "SpecificUsers") {
    return null;
  }

  return (
    <SelectMultiple
      name="sharedVisualization.SpecificUsers"
      label={t("specific_users")}
      choices={users}
      displayChoice={displayUser}
    />
  );
};

const UserPermissions = () => {
  const { t } = useTranslation();
  const { userId } = useParams();
  const { permissions: currentPermissions } = useLogin();
  const [users, setUsers] = useState<User[]>([]);
  const [initialValues, setInitialValues] = useState<PermissionFormValues | null>(null);
  const [targetUser, setTargetUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);
  const [saved, setSaved] = useState(false);
  const simplifiedAvailable = initialValues ? canUseSimplifiedPermissions(initialValues) : false;
  const [simplifiedMode, setSimplifiedMode] = useState(false);

  const titleUser = targetUser && targetUser.id === userId ? targetUser.username?.trim() : "";
  usePageTitle(titleUser ? `${t("user_permissions")}: ${titleUser}` : t("user_permissions"));

  const scopeChoices = useMemo<Choice[]>(
    () => [
      { id: "OnlyMine", display: t("permission_only_mine") },
      { id: "SpecificUsers", display: t("permission_specific_users") },
      { id: "AllCompany", display: t("permission_all_company") },
      { id: "All", display: t("permission_all") },
    ],
    [t],
  );

  useEffect(() => {
    if (!userId || !isAdmin(currentPermissions)) {
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    Promise.all([get_users(), get_user_permissions(userId)])
      .then(([usersPayload, permissionsPayload]) => {
        if (cancelled) return;
        setUsers(usersPayload.items);
        setTargetUser(usersPayload.items.find(user => user.id === userId) ?? null);
        setInitialValues(permissionsToForm(permissionsPayload));
        setLoadError(null);
      })
      .catch((error: unknown) => {
        if (!cancelled) {
          setLoadError(
            error instanceof Error ? error.message : t("failed_to_load_user_permissions"),
          );
        }
      })
      .finally(() => {
        if (!cancelled) {
          setLoading(false);
        }
      });

    return () => {
      cancelled = true;
    };
  }, [currentPermissions, t, userId]);

  useEffect(() => {
    setSimplifiedMode(simplifiedAvailable);
  }, [simplifiedAvailable]);

  if (!isAdmin(currentPermissions)) {
    return (
      <Container>
        <div className="mx-auto max-w-4xl px-4 py-8">
          <ErrorBlockGeneric error={t("you_do_not_have_permission_to_edit_users")} />
        </div>
      </Container>
    );
  }

  return (
    <Container>
      <div className="mx-auto flex max-w-5xl flex-col gap-6 px-4 py-8">
        <div className="space-y-2">
          <Link
            to="/administration/user-management/"
            className="inline-flex text-sm text-primary transition hover:text-primary/80"
          >
            {t("back_to_user_management")}
          </Link>
          <div className="flex items-center gap-3">
            <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-muted-surface text-muted">
              <ShieldCheck className="h-5 w-5" />
            </div>
            <div>
              <h1 className="text-3xl font-semibold text-text">{t("user_permissions")}</h1>
              <p className="text-sm text-text-dim">
                {targetUser ? displayUser(targetUser) : t("user_permissions_description")}
              </p>
            </div>
          </div>
        </div>

        <section className="rounded-2xl border border-outline bg-surface p-5 shadow-sm">
          {loading ? (
            <div className="text-sm text-text-dim">{t("loading_ellipsis")}</div>
          ) : loadError || !initialValues || !targetUser ? (
            <ErrorBlockGeneric error={loadError ?? t("selected_user_not_found")} />
          ) : (
            <Formik<PermissionFormValues>
              initialValues={initialValues}
              enableReinitialize
              onSubmit={async (values, actions) => {
                setSubmitError(null);
                setSaved(false);
                actions.setErrors({});
                try {
                  const updated = await set_user_permissions(
                    targetUser.id,
                    formToPermissions(values, simplifiedMode),
                  );
                  setInitialValues(permissionsToForm(updated));
                  setSaved(true);
                } catch (error: unknown) {
                  const parsed = splitApiRequestFormErrors<PermissionFormValues>(
                    error,
                    t,
                    simplifiedMode
                      ? [
                          "is_admin",
                          "sharedVisualization.mode",
                          "sharedVisualization.SpecificUsers",
                          "users.creation",
                        ]
                      : [
                          "is_admin",
                          "recordings.visualization.mode",
                          "recordings.visualization.SpecificUsers",
                          "processes.visualization.mode",
                          "processes.visualization.SpecificUsers",
                          "screenshots.visualization.mode",
                          "screenshots.visualization.SpecificUsers",
                          "users.visualization.mode",
                          "users.visualization.SpecificUsers",
                          "users.creation",
                        ],
                    simplifiedMode
                      ? {
                          "recordings.visualization.SpecificUsers":
                            "sharedVisualization.SpecificUsers",
                          "processes.visualization.SpecificUsers":
                            "sharedVisualization.SpecificUsers",
                          "screenshots.visualization.SpecificUsers":
                            "sharedVisualization.SpecificUsers",
                          "users.visualization.SpecificUsers": "sharedVisualization.SpecificUsers",
                        }
                      : {},
                  );
                  actions.setTouched(parsed.touched, false);
                  actions.setErrors(parsed.fieldErrors);
                  setSubmitError(parsed.remainder);
                } finally {
                  actions.setSubmitting(false);
                }
              }}
            >
              {({ dirty, isSubmitting }) => (
                <Form className="space-y-4">
                  <label className="flex items-center gap-2 rounded-xl border border-outline bg-muted-surface/30 px-4 py-3 text-sm text-text">
                    <input
                      type="checkbox"
                      className="h-4 w-4 rounded border-outline accent-primary"
                      checked={simplifiedMode}
                      disabled={!simplifiedAvailable || isSubmitting}
                      onChange={event => setSimplifiedMode(event.target.checked)}
                    />
                    <span>{t("simplified_permission_form")}</span>
                  </label>
                  {simplifiedMode ? (
                    <>
                      <div className="rounded-xl border border-outline bg-muted-surface/30 p-4">
                        <Switch name="is_admin" label={t("admin_user")} />
                      </div>
                      <div className="rounded-xl border border-outline bg-muted-surface/30 p-4">
                        <Switch name="users.creation" label={t("can_create_users")} />
                      </div>
                      <section className="space-y-3 rounded-xl border border-outline bg-muted-surface/30 p-4">
                        <h2 className="text-sm font-semibold text-text">{t("permission_scope")}</h2>
                        <Select
                          name="sharedVisualization.mode"
                          label={t("permission_scope")}
                          choices={scopeChoices}
                          required
                        />
                        <SimplifiedSpecificSelector users={users} />
                      </section>
                    </>
                  ) : (
                    <>
                      <div className="rounded-xl border border-outline bg-muted-surface/30 p-4">
                        <Switch name="is_admin" label={t("admin_user")} />
                      </div>
                      <PermissionSection
                        title={t("recordings_permissions")}
                        scopeLabel={t("permission_scope")}
                        usersLabel={t("specific_users")}
                        visualizationName="recordings.visualization"
                        users={users}
                        scopeChoices={scopeChoices}
                      />
                      <PermissionSection
                        title={t("processes_permissions")}
                        scopeLabel={t("permission_scope")}
                        usersLabel={t("specific_users")}
                        visualizationName="processes.visualization"
                        users={users}
                        scopeChoices={scopeChoices}
                      />
                      <PermissionSection
                        title={t("screenshots_permissions")}
                        scopeLabel={t("permission_scope")}
                        usersLabel={t("specific_users")}
                        visualizationName="screenshots.visualization"
                        users={users}
                        scopeChoices={scopeChoices}
                      />
                      <section className="space-y-3 rounded-xl border border-outline bg-muted-surface/30 p-4">
                        <h2 className="text-sm font-semibold text-text">
                          {t("users_permissions")}
                        </h2>
                        <Switch name="users.creation" label={t("can_create_users")} />
                        <Select
                          name="users.visualization.mode"
                          label={t("permission_scope")}
                          choices={scopeChoices}
                          required
                        />
                        <UsersSpecificSelector users={users} />
                      </section>
                    </>
                  )}
                  <div className="flex items-center justify-between gap-3">
                    <div className="min-h-5 text-sm text-text-dim">
                      {saved && t("permissions_updated")}
                    </div>
                    <Button
                      color="primary"
                      type="submit"
                      loading={isSubmitting}
                      disabled={!dirty || isSubmitting}
                    >
                      {t("save_user_permissions")}
                    </Button>
                  </div>
                  <ErrorBlockGeneric error={submitError} />
                </Form>
              )}
            </Formik>
          )}
        </section>
      </div>
    </Container>
  );
};

export default UserPermissions;
